import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { useBack, useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { kindOf, when } from '../stockShared';
import { CompletePlanModal } from './Schedule';

// The truck for an order (the director, 29 Oct 2026; migration 088): 33
// pallet spaces, 15 rows of two and a last row of three, space 1 behind the
// cab; two pallets to a space at most. Drag a pallet to another space, or
// onto one with a pallet to put it on top; only Admin → Packaging's own
// setting stops it, and says why. Click a pallet to mark it done -- packed,
// wrapped and ready to load -- recording in reverse. Three colours: planned
// (grey), being made (amber), done (green; a space all done goes green).
// Prototype: https://claude.ai/artifact/5bDuXo6ovqWXkWESbYaF8i

const ROWS = [...Array.from({ length: 15 }, (_, r) => [2 * r, 2 * r + 1]), [30, 31, 32]];
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-GB');
const STATE_COLOR = { planned: '#6b7280', making: '#f59e0b' };
const STATE_SAID = { planned: 'Planned', making: 'Being made' };
const PART_DOT = { made: '#4ade80', making: '#f59e0b', planned: '#6b7280' };
// What the floor does not need listed under Packaging: the pallet's own wrapping.
const LEAVE_OUT = ['tape', 'wrap', 'cover', 'cover_tape', 'label', 'pallet'];
const canCarry = (s) => s === 'carries' || s === 'both';

const CSS = `
.tk { --bg:#0f1117; --card:#1e2329; --sunk:#161b22; --bord:#2a3040; --text:#f0f2f5; --dim:#9ca3af; --muted:#6b7280; --teal:#14b8a6; --amber:#f59e0b; --green:#4ade80; --red:#f87171; --violet:#a78bfa; --steel:#3b4352; --steel-hi:#566174; }
.tk-main { display:grid; grid-template-columns:minmax(0,560px) minmax(0,1fr); gap:22px; align-items:start; }
@media (max-width:920px) { .tk-main { grid-template-columns:minmax(0,1fr); } }
.tk-truck { position:relative; margin:0 auto; width:min(100%,520px); }
.tk-cab { display:block; width:66%; margin:0 auto -6px; height:auto; }
.tk-trailer { position:relative; background:linear-gradient(180deg,var(--steel-hi),var(--steel)); border-radius:10px 10px 6px 6px; padding:12px; box-shadow:inset 0 0 0 2px #1d2230, 0 10px 30px rgba(0,0,0,.45); }
.tk-deck { background:#232934; border-radius:6px; padding:8px; display:grid; gap:6px; }
.tk-row { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:6px; }
.tk-row.three { grid-template-columns:repeat(3,minmax(0,1fr)); }
.tk-row.three .tk-space { aspect-ratio:2/3; height:auto; }
.tk-wheel { position:absolute; width:10px; height:46px; background:#0b0d12; border-radius:4px; box-shadow:inset 0 0 0 2px #22283a; }
.tk-doors { display:flex; justify-content:space-between; margin:6px 4px 0; color:var(--muted); font-size:11px; letter-spacing:.1em; text-transform:uppercase; }
.tk-space { position:relative; min-width:0; height:96px; box-sizing:border-box; border:1.5px solid #4b5568; background:#2a313d; border-radius:8px; padding:5px 5px 5px 30px; box-shadow:inset 0 1px 0 rgba(255,255,255,.05), 0 1px 2px rgba(0,0,0,.35); transition:border-color .15s, background .15s; }
.tk-space.is-empty { border-style:dashed; background:#252b36; box-shadow:none; display:grid; place-items:center; }
.tk-space.full-done { border-color:rgba(74,222,128,.85); background:rgba(74,222,128,.24); box-shadow:0 0 0 1px rgba(74,222,128,.25), 0 1px 2px rgba(0,0,0,.35); }
.tk-space.drop-ok, .tk-over.drop-ok { border-color:var(--teal); background:rgba(20,184,166,.15); }
.tk-space.drop-no { border-color:var(--red); background:rgba(248,113,113,.12); }
.tk-space.target { cursor:pointer; border-color:var(--teal); }
.tk-num { position:absolute; left:5px; top:5px; min-width:20px; height:20px; padding:0 3px; box-sizing:border-box; border-radius:6px; background:#3b4456; color:var(--text); display:grid; place-items:center; font-size:11.5px; font-weight:800; font-variant-numeric:tabular-nums; }
.tk-space.full-done .tk-num { background:var(--green); color:#0f172a; }
.tk-swap { position:absolute; left:5px; top:30px; width:20px; height:20px; padding:0; border-radius:6px; border:1px solid #56607a; background:#303848; color:var(--text); font-size:12px; line-height:1; cursor:pointer; display:grid; place-items:center; }
.tk-swap:hover, .tk-swap:focus-visible { border-color:var(--teal); color:var(--teal); outline:none; }
.tk-empty { color:#4b5566; font-size:11px; }
.tk-stack { height:100%; min-width:0; display:flex; flex-direction:column-reverse; gap:3px; }
.tk-stack .tk-pallet { flex:1 1 0; min-height:0; overflow:hidden; display:flex; flex-direction:column; justify-content:center; }
.tk-stack .tk-pallet.half { flex:0 0 calc(50% - 2px); }
.tk-room { flex:1 1 0; min-height:0; border:1px dashed #56607a; border-radius:6px; display:grid; place-items:center; color:var(--muted); font-size:10.5px; }
.tk-pallet { position:relative; min-width:0; border-radius:6px; padding:4px 7px; font-size:12px; line-height:1.25; cursor:pointer; background:var(--sunk); border:1px solid var(--bord); border-left:4px solid var(--c,var(--muted)); user-select:none; box-sizing:border-box; color:var(--text); }
.tk-pallet[draggable="true"] { cursor:grab; }
.tk-pallet:focus-visible, .tk-lhead:focus-visible { outline:2px solid var(--teal); outline-offset:2px; }
.tk-pallet .nm { font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.tk-pallet .q { color:var(--dim); font-size:11px; font-variant-numeric:tabular-nums; }
.tk-pallet .q em { font-style:normal; color:var(--violet); }
.tk-pallet.done { background:rgba(74,222,128,.34); border-color:rgba(74,222,128,.8); border-left-color:var(--green); }
.tk-pallet.done .q { color:#d1fae5; }
.tk-pallet.done .nm::after { content:" ✓"; color:var(--green); }
.tk-pallet.dragging { opacity:.4; }
.tk-side { display:flex; flex-direction:column; gap:14px; min-width:0; }
.tk-card { background:var(--card); border:1px solid var(--bord); border-radius:12px; padding:14px 16px; }
.tk-card h2 { font-size:11px; font-weight:800; color:var(--muted); text-transform:uppercase; letter-spacing:.08em; margin:0 0 10px; }
.tk-card.warn { border-color:rgba(245,158,11,.55); }
.tk-over { min-height:64px; border:1.5px dashed #4b5568; border-radius:10px; padding:8px; display:grid; grid-template-columns:repeat(auto-fill,minmax(170px,1fr)); gap:6px; transition:border-color .15s, background .15s; }
.tk-over.target { cursor:pointer; border-color:var(--teal); }
.tk-over .tk-pallet { min-height:46px; display:flex; flex-direction:column; justify-content:center; }
.tk-over .none { color:var(--muted); font-size:12px; grid-column:1/-1; align-self:center; text-align:center; }
.tk-why { color:var(--dim); font-size:12px; line-height:1.5; margin:0 0 8px; }
.tk-figs { display:flex; gap:22px; flex-wrap:wrap; }
.tk-figs .v { font-size:26px; font-weight:800; font-variant-numeric:tabular-nums; }
.tk-figs .l { color:var(--dim); font-size:12px; }
.tk-bar { height:8px; border-radius:99px; background:var(--sunk); overflow:hidden; margin-top:10px; }
.tk-bar > i { display:block; height:100%; background:var(--green); transition:width .3s; }
.tk-line { border-top:1px solid var(--bord); padding-top:8px; margin-top:8px; }
.tk-line:first-child { border-top:0; padding-top:0; margin-top:0; }
.tk-lhead { display:grid; grid-template-columns:12px 10px minmax(0,1fr) auto; gap:8px; align-items:center; width:100%; background:none; border:0; padding:0; color:inherit; font:inherit; text-align:left; cursor:pointer; }
.tk-lhead .chev { color:var(--muted); font-size:11px; transition:transform .15s; }
.tk-lhead[aria-expanded="true"] .chev { transform:rotate(90deg); }
.tk-dot { width:10px; height:10px; border-radius:3px; background:var(--c); }
.tk-lhead b { font-size:13.5px; }
.tk-how { color:var(--dim); font-size:12px; }
.tk-cnt { font-size:12px; text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; border-radius:99px; padding:1px 8px; color:var(--dim); border:1px solid #4b5568; background:rgba(107,114,128,.15); }
.tk-cnt b { font-weight:800; }
.tk-cnt.going { color:#fde68a; border-color:rgba(245,158,11,.8); background:rgba(245,158,11,.20); }
.tk-cnt.all { color:#d1fae5; border-color:rgba(74,222,128,.8); background:rgba(74,222,128,.34); }
.tk-detail { margin:8px 0 2px 30px; display:flex; flex-direction:column; gap:5px; }
.tk-detail .for { font-size:12.5px; color:var(--dim); }
.tk-detail .grp { font-size:10.5px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:var(--muted); margin-top:6px; padding-bottom:2px; border-bottom:1px solid var(--bord); }
.tk-ev { display:grid; grid-template-columns:8px minmax(0,1fr) auto; gap:8px; align-items:baseline; font-size:12.5px; }
.tk-ev > i { width:8px; height:8px; border-radius:50%; background:var(--c); align-self:start; margin-top:5px; }
.tk-ev .nmx { min-width:0; color:var(--text); }
.tk-ev small { display:block; color:var(--muted); font-size:11.5px; }
.tk-ev .amt { font-weight:800; font-variant-numeric:tabular-nums; white-space:nowrap; text-align:right; }
.tk-warn { color:var(--amber); font-weight:600; font-size:11.5px; }
.tk-space.tall-check { border-color:var(--amber); }
.tk-space.tall-too_tall { border-color:var(--red); }
.tk-tall { position:absolute; z-index:3; right:6px; bottom:4px; pointer-events:none; font-size:10px; font-weight:800; padding:0 4px; border-radius:4px; background:rgba(0,0,0,.45); color:var(--amber); }
.tk-space.tall-too_tall .tk-tall { color:var(--red); }
.tk-detail .where { color:var(--dim); font-size:12px; margin-top:4px; }
.tk-rec { display:flex; flex-direction:column; gap:3px; margin-top:3px; }
.tk-rech { font-size:12.5px; font-weight:700; color:var(--text); border-left:3px solid var(--teal); padding-left:8px; }
.tk-rech small { display:inline; font-weight:400; color:var(--muted); font-size:11.5px; margin-left:6px; }
.tk-rec .tk-ev { padding-left:12px; }
.tk-pct { margin-left:6px; color:var(--muted); font-size:11.5px; font-variant-numeric:tabular-nums; }
.tk-ktag { font-size:10.5px; font-weight:600; color:var(--k); border:1px solid var(--k); border-radius:5px; padding:0 5px; margin-left:4px; white-space:nowrap; opacity:.9; }
.tk-runnote { margin:8px 0 2px 30px; padding:8px 10px; border:1px solid rgba(245,158,11,.55); background:rgba(245,158,11,.10); border-radius:8px; font-size:12.5px; color:#fde68a; display:flex; flex-direction:column; gap:6px; }
.tk-runnote .run { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; color:#f0f2f5; }
.tk-legend { display:flex; flex-wrap:wrap; gap:10px 16px; font-size:12px; color:var(--dim); }
.tk-legend span { display:inline-flex; align-items:center; gap:6px; }
.tk-legend i { width:12px; height:12px; border-radius:3px; display:inline-block; }
.tk-pop { position:fixed; z-index:50; background:var(--card); border:1px solid var(--teal); border-radius:12px; padding:12px 14px; width:280px; box-shadow:0 14px 40px rgba(0,0,0,.6); color:var(--text); }
.tk-pop b { font-size:14px; }
.tk-pop .st { color:var(--dim); font-size:12.5px; line-height:1.5; margin:4px 0 10px; }
.tk-pop .row2 { display:flex; gap:8px; flex-wrap:wrap; }
.tk-btn { background:transparent; color:var(--text); border:1px solid var(--bord); border-radius:9px; padding:7px 12px; font:700 12.5px system-ui, sans-serif; cursor:pointer; }
.tk-btn.go { background:var(--teal); color:#0f172a; border-color:var(--teal); }
.tk-btn:disabled { opacity:.6; cursor:default; }
.tk-btn:focus-visible { outline:2px solid var(--teal); outline-offset:2px; }
@media (prefers-reduced-motion: reduce) { .tk * { transition:none !important; } }
`;

// Everything one product (or part sold as it is) takes for the order's
// quantity, from its BOM in Stock: the parts needed, moulded and bought
// together; the material of each moulded part by its recipe; and its own
// packaging (not the tape, film, cover foil, pallet or label).
function everythingFor(b, Q) {
  const items = b.items, fin = b.final_id;
  const r = (v, d) => Math.round(v * 10 ** d) / 10 ** d;
  const amount = (v, unit) => (unit === 'kos' || !unit ? Math.ceil(v - 1e-9) : r(v, v < 10 ? 2 : 1));
  const per = new Map();
  const walk = (id, q, seen) => {
    for (const e of (items[id] && items[id].inputs) || []) {
      const x = Number(e.input_id), n = q * Number(e.qty);
      if (seen.has(x)) continue;
      per.set(x, (per.get(x) || 0) + n);
      // A step's list is walked through; a made-here item is a part needed, its own list is its own.
      if (items[x] && (items[x].kind === 'stage' || items[x].kind === 'moulded')) walk(x, n, new Set([...seen, x]));
    }
  };
  walk(fin, 1, new Set([fin]));
  if (b.kind === 'part') per.set(fin, 1);
  const bought = [], packaging = new Map(), byPart = new Map();
  for (const [id, q] of per) {
    const it = items[id];
    if (!it || it.kind === 'stage' || (id === fin && b.kind !== 'part')) continue;
    if (it.kind === 'moulded' && it.part_id != null) { if (!byPart.has(it.part_id)) byPart.set(it.part_id, []); byPart.get(it.part_id).push({ id, q, per: Number(it.per_part) || 1 }); continue; }
    if (it.kind === 'packaging') { const k = `${it.name}|${it.unit || 'kos'}`; packaging.set(k, (packaging.get(k) || 0) + q * Q); continue; }
    bought.push({ name: it.name, qty: amount(q * Q, it.unit), unit: it.unit || 'kos', kind: it.kind });
  }
  const moulded = [], materials = [];
  for (const [pid, pieces] of byPart) {
    const m = (b.moulding || {})[pid] || {};
    const family = pieces.length > 1;
    const n = (family ? Math.max(...pieces.map(p => p.q / p.per)) : pieces[0].q) * Q;
    const name = m.part || items[pieces[0].id].name;
    moulded.push({ part_id: Number(pid), name, qty: Math.round(n), sets: family, pieces: family ? pieces.map(p => items[p.id].name) : [] });
    const g = family ? m.good?.set?.g : m.good?.piece?.g;
    // The recipe is base 100 % plus what is dosed on top: each its share of the whole, as the BOM counts it.
    const sum = (m.components || []).reduce((t, x) => t + (Number(x.pct) > 0 ? Number(x.pct) : 0), 0) || 100;
    materials.push({
      part: name, recipe: m.recipe || null, colour: m.colour || null,
      rows: (m.components || []).map(c => {
        const share = Number(c.pct) > 0;
        const kg = g && share ? n * g * Number(c.pct) / sum / 1000 : null;
        return { name: c.material, kg: kg == null ? null : r(kg, kg < 10 ? 2 : 1), type: c.type === 'base' ? '' : c.type, pct: share ? Number(c.pct) : null,
          warn: !share ? 'no share set in its recipe' : !g ? `no weight set for ${name}` : '' };
      }),
    });
  }
  for (const l of (b.packing && b.packing.lines) || []) {
    if (l.taken_below || l.per_one == null || LEAVE_OUT.includes(l.what)) continue;
    const unit = l.per_unit === 'g' ? 'kg' : l.per_unit === 'm' ? 'm' : 'kos';
    const k = `${l.name || l.item_name}|${unit}`;
    packaging.set(k, (packaging.get(k) || 0) + l.per_one * Q / (l.per_unit === 'g' ? 1000 : 1));
  }
  return {
    moulded, bought, materials,
    packaging: [...packaging].map(([k, v]) => { const [name, unit] = k.split('|'); return { name, qty: amount(v, unit), unit }; }),
  };
}

const num = (v, u) => `${fmt(v)}${u === 'kos' ? '' : ` ${u}`}`;
const Tag = ({ label, color }) => (label ? <span className="tk-ktag" style={{ '--k': color }}>{label}</span> : null);
function Ev({ dot = 'transparent', name, tag, sub, amt }) {
  return (
    <div className="tk-ev"><i style={{ '--c': dot }} />
      <div className="nmx">{name} {tag}{sub ? <small>{sub}</small> : null}</div>
      <span className="amt">{amt}</span>
    </div>
  );
}

export default function Truck() {
  const { orderId } = useParams();
  const goBack = useBack('/orders');
  const { toast } = useToast();
  const auth = useAuth();
  // Moving pallets: everyone who records stock (the director, 30 Oct 2026).
  const canMove = auth.can('record_stock');
  const record = auth.can('record_stock');
  const canPlan = auth.can('plan');
  const phone = useWindowWidth() < 760;
  const [t, setT] = useState(null);
  const [error, setError] = useState(null);
  const [lay, setLay] = useState(null);                 // { spaces, off } as shown
  const [opened, setOpened] = useState(() => new Set());
  const [builds, setBuilds] = useState({});             // line key -> { data } | { error } | { loading }
  const [pop, setPop] = useState(null);                 // { key, x, y }
  const [moving, setMoving] = useState(null);           // a pallet waiting for a space (tap to move)
  const [dragKey, setDragKey] = useState(null);
  const [over, setOver] = useState(null);               // { at: space index | 'off', ok }
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);              // Arrange again, asked twice
  const [completing, setCompleting] = useState(null);   // a run to mark done, from a line whose pallets are all done
  const saving = useRef({ inFlight: false, pending: null, was: null });

  const accept = useCallback((data, takeLayout) => {
    setT(data);
    saving.current.was = data.saved ? data.saved.at : null;
    if (takeLayout) setLay({ spaces: data.spaces, off: data.off });
  }, []);
  const load = useCallback(() => {
    api.get(`/api/v1/truck/${orderId}`).then(r => { accept(r.data, true); setError(null); })
      .catch(err => setError(err.response?.data?.error || 'The truck could not be read just now. Check the connection and reload.'));
  }, [orderId, accept]);
  useEffect(() => { load(); }, [load]);

  const P = useMemo(() => new Map((t?.pallets || []).map(p => [p.key, p])), [t]);
  const L = useMemo(() => new Map((t?.lines || []).map(l => [l.key, l])), [t]);
  const lineOf = (key) => L.get(P.get(key)?.line) || {};
  const nameOf = (key) => lineOf(key).name || 'A pallet';

  // ── what may stand on what: only Admin → Packaging's own setting ──
  const mayTop = (key) => !!P.get(key)?.box || !lineOf(key).not_top;
  const mayCarry = (key) => !P.get(key)?.box && !lineOf(key).not_under;
  const whyNotTop = (key) => lineOf(key).not_top;
  const whyNotUnder = (key) => (P.get(key)?.box ? lineOf(key).box_under : lineOf(key).not_under);
  const whereIs = (key) => {
    const s = lay.spaces.findIndex(x => x.includes(key));
    return s >= 0 ? { s, pos: lay.spaces[s].indexOf(key) } : { s: -1 };
  };
  function check(key, to) {
    const from = whereIs(key);
    const [b, top] = lay.spaces[to];
    if (from.s === to) return 'It is already there.';
    if (!b) return null;
    if (top) return `Space ${to + 1} has two already. Drag one of them away first, or use an empty space.`;
    if (from.s >= 0 && from.pos === 0 && lay.spaces[from.s][1]) return `${nameOf(key)} has ${nameOf(lay.spaces[from.s][1])} on it, so the two need an empty space.`;
    if (mayTop(key) && mayCarry(b)) return null;
    if (mayTop(b) && mayCarry(key)) return null;
    return !mayTop(key) ? whyNotTop(key) : whyNotUnder(b);
  }

  // Saved as soon as it is moved; moves made while one is saving go next.
  function save(next) {
    setLay(next);
    const s = saving.current;
    if (s.inFlight) { s.pending = next; return; }
    s.inFlight = true;
    const send = async (arr) => {
      try {
        const { data } = await api.put(`/api/v1/truck/${orderId}`, { spaces: arr.spaces, off_truck: arr.off, was: s.was });
        s.was = data.saved ? data.saved.at : null;
        if (s.pending) { const p = s.pending; s.pending = null; setT(data); send(p); return; }
        s.inFlight = false;
        accept(data, true);
      } catch (err) {
        s.inFlight = false; s.pending = null;
        toast.error(err.response?.data?.error || 'The move could not be saved just now. The truck is read again: move it again.');
        load();
      }
    };
    send(next);
  }
  // Out of where it is now; what stood on it stays in its space.
  function lifted(key) {
    const spaces = lay.spaces.map(s => s.slice());
    let off = lay.off.filter(k => k !== key), rider = null;
    const from = whereIs(key);
    if (from.s >= 0) {
      const [b, top] = spaces[from.s];
      if (from.pos === 1) spaces[from.s] = [b, null];
      else { spaces[from.s] = [null, null]; rider = top; }
    }
    return { spaces, off, rider };
  }
  function moveTo(key, to) {
    const why = check(key, to);
    if (why) { toast.error(why); return; }
    const { spaces, off, rider } = lifted(key);
    const under = spaces[to][0];
    if (!under) spaces[to] = [key, rider];
    else if (mayTop(key) && mayCarry(under)) spaces[to] = [under, key];
    else {
      spaces[to] = [key, under];
      toast.success(`${nameOf(key)} went underneath and ${nameOf(under)} on top: ${!mayTop(key) ? whyNotTop(key) : whyNotUnder(under)}`);
    }
    save({ spaces, off });
  }
  function takeOff(key) {
    if (lay.off.includes(key)) return;
    const { spaces, off, rider } = lifted(key);
    save({ spaces, off: [...off, key, ...(rider ? [rider] : [])] });
  }
  function swapIn(i) {
    const [b, top] = lay.spaces[i];
    if (!top) return;
    if (!mayTop(b)) { toast.error(whyNotTop(b)); return; }
    if (!mayCarry(top)) { toast.error(whyNotUnder(top)); return; }
    save({ spaces: lay.spaces.map((s, j) => (j === i ? [top, b] : s)), off: lay.off });
  }
  async function arrangeAgain() {
    if (!sure) { setSure(true); setTimeout(() => setSure(false), 4000); return; }
    setSure(false); setBusy(true);
    try { accept((await api.post(`/api/v1/truck/${orderId}/arrange-again`)).data, true); toast.success('Arranged again: Dose first from behind the cab, then the rest in the order\'s order.'); }
    catch (err) { toast.error(err.response?.data?.error || 'It could not be arranged again just now. Try again.'); }
    setBusy(false);
  }
  async function mark(key, on) {
    setPop(null); setBusy(true);
    try {
      const { data } = await api.post(`/api/v1/truck/${orderId}/${on ? 'done' : 'not-done'}`, { pallet: key });
      accept(data, !saving.current.inFlight);
      toast.success(on ? `${nameOf(key)}: done, ready to load.` : `${nameOf(key)}: no longer done.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'It could not be saved just now. Try again.');
      load();
    }
    setBusy(false);
  }
  async function openPdf() {
    const w = window.open('', '_blank');
    try {
      const r = await api.get(`/api/v1/truck/${orderId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (w) w.location = url; else window.location.href = url;
    } catch {
      if (w) w.close();
      toast.error('The A4 sheet could not be made just now. Try again.');
    }
  }
  function toggle(l) {
    const opening = !opened.has(l.key);
    setOpened(prev => { const next = new Set(prev); if (next.has(l.key)) next.delete(l.key); else next.add(l.key); return next; });
    if (opening && l.build && !builds[l.key]) {
      setBuilds(b => ({ ...b, [l.key]: { loading: true } }));
      api.get(`/api/v1${l.build}`).then(r => setBuilds(b => ({ ...b, [l.key]: { data: r.data } })))
        .catch(err => setBuilds(b => ({ ...b, [l.key]: { error: err.response?.data?.error || 'What it takes could not be read just now.' } })));
    }
  }

  // ── dragging (directors), tapping (everyone) ──
  const dragProps = (key) => (canMove ? {
    draggable: true,
    onDragStart: (e) => { setDragKey(key); setPop(null); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', key); } catch { /* some browsers */ } },
    onDragEnd: () => { setDragKey(null); setOver(null); },
  } : {});
  const dropProps = (at) => (canMove ? {
    onDragOver: (e) => {
      if (!dragKey) return;
      e.preventDefault();
      const ok = at === 'off' ? true : !check(dragKey, at);
      if (!over || over.at !== at || over.ok !== ok) setOver({ at, ok });
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); },
    onDrop: (e) => {
      if (!dragKey) return;
      e.preventDefault();
      const key = dragKey; setDragKey(null); setOver(null);
      if (at === 'off') takeOff(key); else moveTo(key, at);
    },
  } : {});
  const tapTo = (at) => (e) => {
    if (!moving || e.target.closest('.tk-pallet')) return;
    const key = moving; setMoving(null);
    if (at === 'off') takeOff(key); else moveTo(key, at);
  };
  const openPop = (key) => (e) => {
    e.stopPropagation();
    if (moving) {
      // Tapping a pallet while moving: onto its space.
      const at = whereIs(key);
      const m = moving; setMoving(null);
      if (m !== key) { if (at.s >= 0) moveTo(m, at.s); else takeOff(m); }
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(window.innerWidth - 296, Math.max(8, r.left));
    const y = r.bottom + 8 + 220 > window.innerHeight ? Math.max(8, r.top - 8 - 220) : r.bottom + 8;
    setPop({ key, x, y });
  };
  useEffect(() => {
    const close = (e) => { if (!e.target.closest('.tk-pop') && !e.target.closest('.tk-pallet')) setPop(null); };
    const esc = (e) => { if (e.key === 'Escape') { setPop(null); setMoving(null); } };
    document.addEventListener('click', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', esc); };
  }, []);

  if (error) return <div className="tk" style={{ padding: 24, color: '#f87171', fontFamily: 'system-ui', background: '#0f1117', minHeight: '100vh' }}>{error}</div>;
  if (!t || !lay) return <div className="tk" style={{ padding: 24, color: '#9ca3af', fontFamily: 'system-ui', background: '#0f1117', minHeight: '100vh' }}>Reading the truck…</div>;

  const o = t.order;
  const title = [o.customer, o.week ? `Week ${o.week}` : null].filter(Boolean).join(' · ') || `Order ${o.number || o.id}`;
  const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : null);
  const dated = o.dispatch_date ? `lorry leaves ${day(o.dispatch_date)}` : o.delivery_date ? `delivery ${day(o.delivery_date)}` : o.week ? `delivery week ${o.week}` : 'no delivery date yet';
  const color = (p) => (p.done ? 'var(--green)' : STATE_COLOR[L.get(p.line)?.state] || 'var(--muted)');
  const what = (p) => (p.box ? `${fmt(p.qty)} · ${p.box} loose box${p.box === 1 ? '' : 'es'}` : `${fmt(p.qty)}${p.n > 1 ? ` · ${p.k}/${p.n}` : ''}`);
  // A plain function, not a component: a component made here would be new on every
  // render, and React would put a fresh element in mid-drag.
  const pallet = (k, { top = false, half = false } = {}) => {
    const p = P.get(k); if (!p) return null;
    const l = L.get(p.line) || {};
    return (
      <div key={k} className={`tk-pallet${half ? ' half' : ''}${p.done ? ' done' : ''}${dragKey === k ? ' dragging' : ''}`} role="button" tabIndex={0} style={{ '--c': color(p) }}
        title={`${l.name} · ${p.box ? 'loose boxes' : `pallet ${p.k} of ${p.n}`}`} onClick={openPop(k)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPop(k)(e); } }} {...dragProps(k)}>
        <div className="nm">{l.name}</div>
        <div className="q">{top ? <><em>on top</em> · </> : null}{what(p)}</div>
      </div>
    );
  };
  const dropCls = (at) => (over && over.at === at ? (over.ok ? ' drop-ok' : ' drop-no') : '') + (moving ? ' target' : '');
  const offCount = lay.off.length;
  const onBoard = t.pallets.length - offCount;
  const used = lay.spaces.filter(s => s[0]).length;
  const doneN = t.pallets.filter(p => p.done).length;
  const spacesOf = (lineKey) => lay.spaces.map((s, i) => (s.some(k => k && P.get(k)?.line === lineKey) ? i + 1 : null)).filter(Boolean);
  // Two pallets stacked taller than a lorry, by space number (the server's
  // check against the lorry's inside height, 3 Nov 2026).
  const stackAt = new Map((t.stacks || []).map(x => [x.space, x]));

  const pp = pop && P.get(pop.key);
  const pl = pp && L.get(pp.line);
  const pat = pp && whereIs(pop.key);

  return (
    <div className="tk" style={{ padding: phone ? '14px 12px 90px' : '20px 24px 60px', fontFamily: 'system-ui, sans-serif', color: '#f0f2f5', background: '#0f1117', minHeight: '100vh', boxSizing: 'border-box', maxWidth: 1180, margin: '0 auto' }}>
      <style>{CSS}</style>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 8 }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: 10, marginRight: 4 }} />Back
      </button>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: phone ? 20 : 24, fontWeight: 800, margin: 0 }}>{title}</h1>
          <div style={{ color: '#9ca3af', marginTop: 3, fontSize: 14 }}>Order {o.number || o.id} · {dated}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="tk-btn" onClick={openPdf} title="The truck as it is arranged, on one A4 page to print for the floor"><i className="fa-solid fa-file-pdf" style={{ marginRight: 6 }} />A4 sheet</button>
          {canMove && !t.auto && (
            <button className="tk-btn" onClick={arrangeAgain} disabled={busy} title="Dose first from behind the cab, then the rest in the order's order">
              {sure ? 'Sure? Arrange again' : 'Arrange again'}
            </button>
          )}
        </div>
      </div>
      <p style={{ color: '#6b7280', fontSize: 12.5, lineHeight: 1.5, margin: '0 0 14px', maxWidth: 900 }}>
        {t.auto ? 'Arranged by the app: Dose first from behind the cab, then the rest in the order\'s order.' : `Last moved by ${t.saved.by || 'someone'} on ${when(t.saved.at)}.`}
        {t.added?.length && !t.auto ? ` ${t.added.length} pallet${t.added.length === 1 ? '' : 's'} new on the order since then took the first free space${t.added.length === 1 ? '' : 's'}.` : ''}
        {' '}{canMove ? 'Drag a pallet to another space, or onto a space with one pallet to put it on top; ⇅ swaps which is on top. ' : ''}
        {record ? 'Click a pallet to mark it done: packed, wrapped and ready to load.' : 'Click a pallet to see how far it is.'}
      </p>

      <div className="tk-main">
        <div>
          <div className="tk-truck">
            <svg className="tk-cab" viewBox="0 0 340 150" aria-hidden="true">
              <defs><linearGradient id="tk-cabg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1fb5a3" /><stop offset="1" stopColor="#0e7f74" /></linearGradient></defs>
              <rect x="18" y="58" width="16" height="40" rx="5" fill="#0b0d12" /><rect x="306" y="58" width="16" height="40" rx="5" fill="#0b0d12" />
              <rect x="40" y="10" width="260" height="134" rx="34" fill="url(#tk-cabg)" />
              <path d="M70 32 Q170 12 270 32 L262 70 Q170 58 78 70 Z" fill="#0f1117" opacity=".85" />
              <rect x="30" y="40" width="14" height="26" rx="4" fill="#0f1117" /><rect x="296" y="40" width="14" height="26" rx="4" fill="#0f1117" />
              <rect x="80" y="86" width="180" height="10" rx="5" fill="#0a5f57" /><rect x="80" y="104" width="180" height="10" rx="5" fill="#0a5f57" />
              <text x="170" y="134" textAnchor="middle" fontFamily="system-ui" fontSize="13" fontWeight="800" fill="#d8fffa" letterSpacing="3">CAB</text>
            </svg>
            <div className="tk-trailer">
              <div className="tk-wheel" style={{ left: -6, top: '18%' }} /><div className="tk-wheel" style={{ right: -6, top: '18%' }} />
              <div className="tk-wheel" style={{ left: -6, bottom: '6%' }} /><div className="tk-wheel" style={{ right: -6, bottom: '6%' }} />
              <div className="tk-wheel" style={{ left: -6, bottom: 'calc(6% + 54px)' }} /><div className="tk-wheel" style={{ right: -6, bottom: 'calc(6% + 54px)' }} />
              <div className="tk-deck">
                {ROWS.map(row => (
                  <div key={row[0]} className={`tk-row${row.length === 3 ? ' three' : ''}`}>
                    {row.map(i => {
                      const [b, top] = lay.spaces[i];
                      if (!b) return (
                        <div key={i} className={`tk-space is-empty${dropCls(i)}`} {...dropProps(i)} onClick={tapTo(i)}>
                          <span className="tk-num">{i + 1}</span><span className="tk-empty">empty</span>
                        </div>
                      );
                      const all = [b, top].filter(Boolean).map(k => P.get(k));
                      const full = all.every(p => p && p.done);
                      const roomy = !top && !P.get(b)?.box && canCarry(lineOf(b).stacking);
                      const tall = top ? stackAt.get(i + 1) : null;
                      return (
                        <div key={i} className={`tk-space${full ? ' full-done' : ''}${tall ? ` tall-${tall.level}` : ''}${dropCls(i)}`} {...dropProps(i)} onClick={tapTo(i)}
                          title={tall ? tall.says : undefined}>
                          <span className="tk-num">{i + 1}</span>
                          {tall && tall.mm ? <span className="tk-tall">{tall.tag}</span> : null}
                          {top && canMove ? <button type="button" className="tk-swap" onClick={(e) => { e.stopPropagation(); swapIn(i); }} title="Swap which is on top" aria-label={`Swap which is on top in space ${i + 1}`}>⇅</button> : null}
                          <div className="tk-stack">
                            {pallet(b, { half: roomy })}
                            {top ? pallet(top, { top: true }) : null}
                            {roomy ? <div className="tk-room">room on top</div> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className="tk-doors"><span>◂ left door</span><span>doors</span><span>right door ▸</span></div>
          </div>
        </div>

        <div className="tk-side">
          {(offCount > 0 || canMove) && (
            <div className={`tk-card${offCount ? ' warn' : ''}`}>
              <h2>{offCount ? `Not on the truck · ${offCount}` : 'Not on the truck'}</h2>
              {offCount ? <p className="tk-why">{canMove ? 'These have no space yet. Drag one onto an empty space, or onto a space with one pallet to put it on top.' : 'These have no space on the truck yet.'}</p> : null}
              <div className={`tk-over${dropCls('off')}`} {...dropProps('off')} onClick={tapTo('off')}>
                {offCount ? lay.off.map(k => pallet(k)) : <span className="none">Everything is on the truck. Drag a pallet here to take it off.</span>}
              </div>
            </div>
          )}
          {(t.stacks || []).length > 0 && (
            <div className="tk-card warn">
              <h2>Stacks against the lorry · {t.stacks.length}</h2>
              {t.stacks.map(x => <p key={x.space} className="tk-why">{x.says}</p>)}
            </div>
          )}
          {t.gaps.length > 0 && (
            <div className="tk-card warn">
              <h2>Not counted</h2>
              {t.gaps.map(g => <p key={g.line} className="tk-why">{g.says} <Link to="/admin/packaging" style={{ color: '#14b8a6' }}>Admin → Packaging</Link></p>)}
            </div>
          )}
          <div className="tk-card">
            <h2>On board</h2>
            <div className="tk-figs">
              <div><div className="v">{onBoard}</div><div className="l">pallets on board</div></div>
              <div><div className="v">{used}</div><div className="l">of {t.totals.spaces} spaces</div></div>
              <div><div className="v">{doneN}</div><div className="l">done</div></div>
            </div>
            <div className="tk-bar" aria-hidden="true"><i style={{ width: `${t.pallets.length ? (doneN / t.pallets.length) * 100 : 0}%` }} /></div>
          </div>
          <div className="tk-card">
            <h2>What goes on, and where it stands</h2>
            {t.lines.map(l => {
              const ps = t.pallets.filter(p => p.line === l.key), d = ps.filter(p => p.done).length;
              const isOpen = opened.has(l.key);
              const allDone = ps.length > 0 && d === ps.length;
              const tag = !ps.length ? null : allDone ? <span className="tk-cnt all">Done · <b>{d}/{ps.length}</b></span>
                : d > 0 ? <span className="tk-cnt going">In progress · <b>{d}/{ps.length}</b> done</span> : <span className="tk-cnt"><b>0/{ps.length}</b> done</span>;
              const where = spacesOf(l.key);
              const left = ps.filter(p => lay.off.includes(p.key)).length;
              const said = [where.length ? `space${where.length === 1 ? '' : 's'} ${where.join(', ')}` : '', left ? `${left} not on the truck` : ''].filter(Boolean).join(' · ');
              const how = l.gap ? 'pallets not counted' : `${l.pallets} pallet${l.pallets === 1 ? '' : 's'} of ${fmt(l.per)}${l.rides ? ` + ${l.rides.boxes} loose box${l.rides.boxes === 1 ? '' : 'es'}` : ''}`;
              return (
                <div key={l.key} className="tk-line">
                  <button type="button" className="tk-lhead" aria-expanded={isOpen} onClick={() => toggle(l)}>
                    <span className="chev" aria-hidden="true">▸</span>
                    <span className="tk-dot" style={{ '--c': allDone ? 'var(--green)' : STATE_COLOR[l.state] }} />
                    <span style={{ minWidth: 0 }}><b>{l.name}</b> <span className="tk-how">{fmt(l.qty)} · {how}</span></span>
                    {tag}
                  </button>
                  {/* Every pallet done, its run still open on the board: offer the
                      board's own Complete (the director, 2 Nov 2026). */}
                  {allDone && (l.open_runs || []).length > 0 && (
                    <div className="tk-runnote">
                      <span>Every pallet is done, but {l.open_runs.length === 1 ? 'its run is' : 'its runs are'} still open on the board.</span>
                      {l.open_runs.map(o => (
                        <div key={o.plan_id} className="run">
                          <span>{o.part} · {o.started ? 'on the machine' : 'on the board'} {o.when}</span>
                          {canPlan
                            ? <button type="button" className="tk-btn" style={{ padding: '4px 10px' }} onClick={() => setCompleting({ planId: o.plan_id, partName: o.part, totalQty: 0, goodPartsMade: 0 })}>Mark the run done</button>
                            : <span style={{ color: '#9ca3af' }}>A director marks it done on the board.</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {isOpen && <LineDetail l={l} build={builds[l.key]} said={said} onUndo={(k) => mark(k, false)} record={record} busy={busy} />}
                </div>
              );
            })}
          </div>
          <div className="tk-card">
            <h2>Colours</h2>
            <div className="tk-legend">
              <span><i style={{ background: 'var(--muted)' }} />Planned</span>
              <span><i style={{ background: 'var(--amber)' }} />Being made</span>
              <span><i style={{ background: 'var(--green)' }} />Done: packed, wrapped, ready to load</span>
            </div>
          </div>
        </div>
      </div>

      {completing && (
        <CompletePlanModal plan={completing} toast={toast} onClose={() => setCompleting(null)} onDone={() => { setCompleting(null); load(); }} />
      )}

      {pp && (
        <div className="tk-pop" style={{ left: pop.x, top: pop.y }} role="dialog" aria-label={pl.name}>
          <b>{pl.name}</b>
          <div className="st">
            {pp.box ? `${pp.box} loose box${pp.box === 1 ? '' : 'es'}` : `Pallet ${pp.k} of ${pp.n}`} · {fmt(pp.qty)} · {pat.s >= 0 ? `space ${pat.s + 1}${pat.pos === 1 ? ', on top' : ''}` : 'not on the truck'}<br />
            {pp.done ? `Done: packed, wrapped and ready to load. Marked by ${pp.done.by || 'someone'} on ${when(pp.done.at)}.` : `${STATE_SAID[pl.state]}: ${pl.how}`}
            {!pp.done && record && pl.no_item ? <><br /><span className="tk-warn">{pl.no_item}</span></> : null}
          </div>
          <div className="row2">
            {record && (pp.done
              ? <button type="button" className="tk-btn" disabled={busy || !!pp.done.invoice_id} onClick={() => mark(pop.key, false)} title={pp.done.invoice_id ? 'It went out on a lorry' : ''}>Not done after all</button>
              : !pl.no_item && <button type="button" className="tk-btn go" disabled={busy} onClick={() => mark(pop.key, true)} autoFocus>Mark done</button>)}
            {canMove && <button type="button" className="tk-btn" onClick={() => { setMoving(pop.key); setPop(null); toast.success(`Now tap a space for ${pl.name}.`); }}>Move…</button>}
            {canMove && pat.s >= 0 && lay.spaces[pat.s][1] && <button type="button" className="tk-btn" onClick={() => { setPop(null); swapIn(pat.s); }}>{pat.pos === 1 ? 'Put it underneath' : 'Put it on top'}</button>}
            {canMove && pat.s >= 0 && <button type="button" className="tk-btn" onClick={() => { setPop(null); takeOff(pop.key); }}>Off the truck</button>}
            <button type="button" className="tk-btn" onClick={() => setPop(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// An opened line: everything it takes for the order, and where it is on the truck.
function LineDetail({ l, build, said, onUndo, record, busy }) {
  const where = <div className="where">On the truck: {said || 'not placed yet'}</div>;
  const stray = l.stray_done?.length ? (
    <div className="tk-warn" style={{ marginTop: 4 }}>
      {l.stray_done.length} pallet{l.stray_done.length === 1 ? ' was' : 's were'} marked done that the order no longer has (it has {l.pallets} now).
      {record && l.stray_done.map(s => <button key={s.key} type="button" className="tk-btn" style={{ marginLeft: 8, padding: '2px 8px' }} disabled={busy} onClick={() => onUndo(s.key)}>Not done after all ({s.key.split('#')[1]})</button>)}
    </div>
  ) : null;
  const statusOf = new Map(l.parts.map(p => [Number(p.part_id), p]));
  const simple = (
    <>
      <div className="grp">Parts needed</div>
      {l.parts.map(p => <Ev key={p.part_id} dot={PART_DOT[p.state]} name={p.name} tag={<Tag label="Moulded here" color={kindOf('moulded').color} />} sub={p.when} amt={fmt(p.qty)} />)}
    </>
  );
  if (!l.build || !build || build.loading || build.error) {
    return (
      <div className="tk-detail">
        <div className="for">{build?.loading ? 'Reading everything it takes…' : build?.error ? build.error : `For ${fmt(l.qty)}`}</div>
        {simple}{where}{stray}
      </div>
    );
  }
  const e = everythingFor(build.data, l.qty);
  return (
    <div className="tk-detail">
      <div className="for">Everything it takes for <b>{fmt(l.qty)}</b></div>
      {(e.moulded.length > 0 || e.bought.length > 0) && <div className="grp">Parts needed</div>}
      {e.moulded.map(m => {
        const st = statusOf.get(m.part_id);
        return <Ev key={`m${m.part_id}`} dot={PART_DOT[st?.state || 'planned']} name={m.name} tag={<Tag label="Moulded here" color={kindOf('moulded').color} />}
          sub={[m.pieces.length ? m.pieces.join(', ') : '', st?.when || ''].filter(Boolean).join(' · ')} amt={`${fmt(m.qty)}${m.sets ? ' sets' : ''}`} />;
      })}
      {e.bought.map(x => <Ev key={`b${x.name}`} name={x.name} tag={<Tag label={kindOf(x.kind).label} color={kindOf(x.kind).color} />} amt={num(x.qty, x.unit)} />)}
      {e.materials.length > 0 && <div className="grp">Materials</div>}
      {e.materials.map(m => (
        <div key={m.part} className="tk-rec">
          <div className="tk-rech">Recipe: {m.recipe || 'not set in Admin → Parts'}<small>for {m.part}{m.colour ? ` · ${m.colour}` : ''}</small></div>
          {m.rows.map(r => (
            <div key={r.name} className="tk-ev"><i style={{ '--c': 'transparent' }} />
              <div className="nmx">{r.name} <Tag label={r.type} color="#94a3b8" /><span className="tk-pct">{r.pct == null ? '—' : `${r.pct.toLocaleString('en-GB')} %`}</span>{r.warn ? <small><span className="tk-warn">{r.warn}</span></small> : null}</div>
              <span className="amt">{r.kg == null ? <span className="tk-warn">—</span> : `${r.kg.toLocaleString('en-GB')} kg`}</span>
            </div>
          ))}
        </div>
      ))}
      {e.packaging.length > 0 && <div className="grp">Packaging</div>}
      {e.packaging.map(x => <Ev key={`p${x.name}`} name={x.name} amt={num(x.qty, x.unit)} />)}
      {where}{stray}
    </div>
  );
}

// Today's Lorries button (the director, 30 Oct 2026: "make it a button next
// to +Start new job, but colour it differently"): the next lorries, soonest
// first, each with how far its truck is; one click opens it.
export function NextLorries() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      setError(null);
      api.get('/api/v1/truck?next=4').then(r => setList(r.data))
        .catch(err => setError(err.response?.data?.error || 'The lorries could not be read just now. Try again.'));
    }
  }
  const day = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  const tag = (T) => {
    const all = T.all > 0 && T.done === T.all, some = T.done > 0;
    return { fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 99, padding: '1px 8px', fontVariantNumeric: 'tabular-nums',
      color: all ? '#d1fae5' : some ? '#fde68a' : '#9ca3af', border: `1px solid ${all ? 'rgba(74,222,128,.8)' : some ? 'rgba(245,158,11,.8)' : '#4b5568'}`,
      background: all ? 'rgba(74,222,128,.34)' : some ? 'rgba(245,158,11,.20)' : 'rgba(107,114,128,.15)' };
  };
  const note = { padding: 10, color: '#9ca3af', fontSize: 12.5 };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={toggle} aria-expanded={open} title="The next lorries: where each pallet stands, and which are done"
        style={{ background: 'rgba(56,189,248,0.12)', color: '#7dd3fc', border: '1px solid rgba(56,189,248,0.7)', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <i className="fa-solid fa-truck-ramp-box" style={{ marginRight: 6 }} />Lorries
      </button>
      {open && (
        <div role="menu" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 40, width: 310, maxWidth: 'calc(100vw - 24px)', background: '#1e2329', border: '1px solid rgba(56,189,248,0.6)', borderRadius: 10, padding: 6, boxShadow: '0 14px 40px rgba(0,0,0,.6)' }}>
          {error ? <div style={{ ...note, color: '#f87171' }}>{error}</div>
            : !list ? <div style={note}>Reading the next lorries…</div>
              : !list.length ? <div style={note}>No open orders, so no lorry to load.</div>
                : list.map(o => (
                  <button key={o.id} role="menuitem" onClick={() => navigate(`/orders/${o.id}/truck`)}
                    style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8, alignItems: 'center', width: '100%', background: 'none', border: 0, borderRadius: 8, padding: '8px 10px', color: '#f0f2f5', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}>
                    <span style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 13 }}>{[o.short || o.customer, o.week ? `Week ${o.week}` : o.number].filter(Boolean).join(' · ')}</b><br />
                      <span style={{ color: '#9ca3af', fontSize: 11.5 }}>{o.day && o.day < '9999' ? day(o.day) : 'no date yet'} · {o.totals.on_board} pallet{o.totals.on_board === 1 ? '' : 's'}{o.off ? ` · ${o.off} not on the truck` : ''}</span>
                      {o.stacks_said ? <><br /><span style={{ color: '#f59e0b', fontSize: 11.5, fontWeight: 600 }}>{o.stacks_said}</span></> : null}
                    </span>
                    <span style={tag(o.totals)}>{o.totals.done}/{o.totals.all} done</span>
                  </button>
                ))}
        </div>
      )}
    </div>
  );
}
